import { useConfirm } from "../confirm/ConfirmProvider";
import { toast } from "../toast/toast";
import { useEffect, useState } from "react";
import { Lab, StorageLocation } from "../types";
import {
  addStorageLocation,
  deleteStorageLocation,
  editStorageLocation,
  getStorageLocations,
  setStorageKeyManaged,
} from "../fetch/StorageRequest";
import ActionIconButton from "./ActionIconButton";
import styles from "../styles/modules/StorageLocationPopup.module.css";

interface StorageLocationPopupProps {
  lab: Lab;
  onClose: () => void;
}

function StorageLocationPopup({ lab, onClose }: StorageLocationPopupProps) {
  const confirm = useConfirm();
  const [locations, setLocations] = useState<StorageLocation[]>([]);
  const [newName, setNewName] = useState<string>("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>("");

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lab.id]);

  async function load() {
    setLocations(await getStorageLocations(lab.id));
  }

  async function handleAdd() {
    const name = newName.trim();
    if (!name) return;
    try {
      await addStorageLocation(lab.id, name);
      setNewName("");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "追加に失敗しました");
    }
  }

  function startEdit(loc: StorageLocation) {
    setEditingId(loc.id);
    setEditingName(loc.name);
  }

  async function saveEdit(id: number) {
    const name = editingName.trim();
    if (!name) return;
    try {
      await editStorageLocation(id, name);
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "更新に失敗しました");
    }
  }

  // 鍵管理のON/OFF。ONにすると、その試薬庫の試薬は鍵を借りた人だけが使用できる。
  async function handleToggleKeyManaged(loc: StorageLocation) {
    const next = !loc.key_managed;
    if (
      next === false &&
      !(await confirm(
        "鍵管理をOFFにします。この試薬庫の試薬は鍵がなくても使用できるようになります。よろしいですか？",
        { confirmText: "OFFにする" }
      ))
    ) {
      return;
    }

    try {
      await setStorageKeyManaged(loc.id, next);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "鍵管理の変更に失敗しました");
    }
  }

  async function handleDelete(id: number) {
    if (
      !(await confirm(
        "この保管場所を削除しますか？\n（この保管場所が設定されている試薬は「未設定」になります）",
        { danger: true, confirmText: "削除" }
      ))
    )
      return;
    await deleteStorageLocation(id);
    await load();
  }

  return (
    <div className={styles["overlay"]} onClick={onClose}>
      <div className={styles["panel"]} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles["title"]}>{lab.name}の保管場所（試薬庫）</h3>

        <div className={styles["add-row"]}>
          <input
            type="text"
            placeholder="保管場所名を入力（例：冷蔵庫A）"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          />
          <button className={styles["add-btn"]} onClick={handleAdd}>
            追加
          </button>
        </div>

        <ul className={styles["list"]}>
          {locations.length === 0 && (
            <li className={styles["empty"]}>保管場所がありません</li>
          )}
          {locations.map((loc) => (
            <li key={loc.id} className={styles["item"]}>
              {editingId === loc.id ? (
                <>
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveEdit(loc.id)}
                    placeholder="保管場所名"
                    autoFocus
                  />
                  <div className={styles["item-actions"]}>
                    <button
                      className={styles["save-btn"]}
                      onClick={() => saveEdit(loc.id)}
                    >
                      保存
                    </button>
                    <button
                      className={styles["cancel-btn"]}
                      onClick={() => setEditingId(null)}
                    >
                      取消
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span className={styles["name"]}>{loc.name}</span>
                  <div className={styles["item-actions"]}>
                    <button
                      type="button"
                      className={`${styles["key-switch"]} ${
                        loc.key_managed ? styles["on"] : ""
                      }`}
                      onClick={() => handleToggleKeyManaged(loc)}
                      title="鍵管理"
                      aria-label="鍵管理"
                      aria-pressed={loc.key_managed}
                    >
                      <i />
                      鍵管理
                    </button>
                    <ActionIconButton
                      variant="edit"
                      onClick={() => startEdit(loc)}
                    />
                    <ActionIconButton
                      variant="delete"
                      onClick={() => handleDelete(loc.id)}
                    />
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        <button className={styles["close-btn"]} onClick={onClose}>
          閉じる
        </button>
      </div>
    </div>
  );
}

export default StorageLocationPopup;
